// apps/client/src/world/objects/mailbox.ts — a plot's mailbox in its owner's friend dye (cream when nobody lives there).
// M1-T17: the flag goes up while your own mailbox holds unread mail (perResident, so it follows `you.mail`): a 250 ms ease from down
// (the builder's π/2) to up (0), stepped from the flag mesh's onBeforeRender. Only your own flag ever moves for you, so every other
// mailbox joins the static batch, and yours is baked into one body and one flag mesh (spec §8.7, M2 playtest: five draws a mailbox,
// twice with shadows, put a lived-in town over the draw-call budget).
import { friendColors } from '@whiskerton/content';
import type { ResidentId, WorldObject } from '@whiskerton/shared';
import * as THREE from 'three';
import { bakeMeshes } from '../bake';
import { unreadMail } from '../../state/slices/mail';
import type { ObjectView, ViewCtx } from '../objectView';

const DOWN = Math.PI / 2, UP = 0, EASE_MS = 250;
const ease = (k: number) => 1 - (1 - k) ** 3;

/** True when this mailbox stands on your plot. */
function isYours(obj: WorldObject, ctx: Pick<ViewCtx, 'you' | 'residents'>): boolean {
  const you = ctx.you, plot = (obj.state as { plot?: number }).plot;
  const mine = you ? ctx.residents[you.resident] : undefined;
  return !!mine && typeof plot === 'number' && plot === mine.plot;
}
/** True when this is your mailbox and you have unread letters. */
export function flagUp(obj: WorldObject, ctx: Pick<ViewCtx, 'you' | 'residents'>): boolean {
  return isYours(obj, ctx) && unreadMail(ctx.you!.mail) > 0;
}
/** Bakes `o` into one body mesh and its flag group into one mesh, in place (the groups and their transforms stay). */
function bake(o: THREE.Object3D, flag: THREE.Object3D): THREE.Mesh | null {
  const under = (m: THREE.Object3D) => { for (let p: THREE.Object3D | null = m; p; p = p.parent) if (p === flag) return true; return false; };
  const body = bakeMeshes(o, { skip: under, castShadow: true }), flagged = bakeMeshes(flag, { castShadow: true });
  const old: THREE.Object3D[] = [];
  o.traverse((c) => { if ((c as THREE.Mesh).isMesh && !c.userData.blob) old.push(c); });
  for (const c of old) c.removeFromParent();
  o.add(...body);
  flag.add(...flagged);
  return flagged[0] ?? null;
}

interface FlagAnim { flag: THREE.Object3D; from: number; to: number; start: number }
function animate(view: THREE.Object3D, up: boolean): void {
  const a = view.userData.flagAnim as FlagAnim | undefined;
  if (!a) return;
  const to = up ? UP : DOWN;
  if (a.to === to) return;
  a.from = a.flag.rotation.x; a.to = to; a.start = performance.now();
}

export const view: ObjectView = {
  kinds: ['mailbox'],
  perResident: true,
  async build(obj, ctx) {
    const owner = obj.owner ? ctx.residents[obj.owner as ResidentId] : undefined;
    const up = flagUp(obj, ctx);
    const o = await ctx.assets.model(obj.catalogId, { color: owner ? friendColors[owner.color].dye : '#FFF4E2', flag: up });
    if (!isYours(obj, ctx)) { o.userData.static = true; return o; }
    const flag = o.getObjectByName('flag');
    const mesh = flag ? bake(o, flag) : null;
    if (flag && mesh) {
      const a: FlagAnim = { flag, from: flag.rotation.x, to: flag.rotation.x, start: 0 };
      o.userData.flagAnim = a;
      mesh.onBeforeRender = () => {
        if (a.flag.rotation.x === a.to) return;
        const k = Math.min(1, (performance.now() - a.start) / EASE_MS);
        a.flag.rotation.x = k >= 1 ? a.to : a.from + (a.to - a.from) * ease(k);
      };
    }
    return o;
  },
  update(obj, v, ctx) { animate(v, flagUp(obj, ctx)); },
};
